import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Autocomplete,
  Chip,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  Add,
  Delete,
  Search,
  Calculate,
  Save,
  ArrowBack,
  AutoAwesome,
  Visibility,
  Edit
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import medicalCodingService from '../../../services/medicalCodingService'
import mrdService from '../../../services/mrdservice'
import ICDSearch from './ICDSearch'
import DRGCalculator from './DRGCalculator'

const CodingForm = ({ fileId, onBack, existingCoding = null, mode = 'edit' }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [mrdFile, setMrdFile] = useState(null)
  const [formData, setFormData] = useState({
    primaryDiagnosis: {
      icd10Code: '',
      icd10Description: '',
      diagnosis: '',
      presentOnAdmission: true
    },
    secondaryDiagnoses: [],
    procedures: [],
    externalCauses: [],
    healthStatusFactors: [],
    drgGroup: null,
    aprDrg: null,
    complications: [],
    comorbidities: [],
    hospitalAcquiredConditions: []
  })
  
  const [openICDSearch, setOpenICDSearch] = useState(false)
  const [openDRGCalc, setOpenDRGCalc] = useState(false)
  const [searchType, setSearchType] = useState('primary') // primary, secondary, procedure
  const [autoSuggestions, setAutoSuggestions] = useState([])

  // Read-only props for view mode
  const isReadOnly = mode === 'view'
  const fieldProps = isReadOnly ? { 
    InputProps: { readOnly: true },
    disabled: false // Keep enabled for copy functionality
  } : {}

  useEffect(() => {
    if (fileId) {
      loadMRDFile()
      loadExistingCoding()
      getAutoSuggestions()
    }
  }, [fileId])

  useEffect(() => {
    if (existingCoding) {
      setFormData(existingCoding)
    }
  }, [existingCoding])

  const loadMRDFile = async () => {
    try {
      const response = await mrdService.getFileById(fileId)
      setMrdFile(response.data.data)
    } catch (error) {
      enqueueSnackbar('Failed to load MRD file', { variant: 'error' })
    }
  }

  const loadExistingCoding = async () => {
    try {
      const response = await medicalCodingService.getCodingByFile(fileId)
      if (response.data.success) {
        setFormData(response.data.data)
      }
    } catch (error) {
      // No existing coding found - this is normal for new coding
      console.log('No existing coding found')
    }
  }

  const getAutoSuggestions = async () => {
    try {
      const response = await medicalCodingService.autoSuggestICDCodes({ fileId })
      setAutoSuggestions(response.data.data || [])
    } catch (error) {
      console.log('Auto suggestions not available')
    }
  }

  const handleICDSelect = (icdData) => {
    if (searchType === 'primary') {
      setFormData(prev => ({
        ...prev,
        primaryDiagnosis: {
          ...prev.primaryDiagnosis,
          icd10Code: icdData.code,
          icd10Description: icdData.description
        }
      }))
    } else if (searchType === 'secondary') {
      setFormData(prev => ({
        ...prev,
        secondaryDiagnoses: [
          ...prev.secondaryDiagnoses,
          {
            icd10Code: icdData.code,
            icd10Description: icdData.description,
            diagnosis: '',
            presentOnAdmission: true
          }
        ]
      }))
    } else if (searchType === 'procedure') {
      setFormData(prev => ({
        ...prev,
        procedures: [
          ...prev.procedures,
          {
            icd10pcsCode: icdData.code,
            description: icdData.description,
            procedureDate: new Date().toISOString().split('T')[0],
            surgeon: '',
            assistantSurgeon: ''
          }
        ]
      }))
    }
    setOpenICDSearch(false)
  }

  const handleCalculateDRG = async () => {
    try {
      setLoading(true)
      const response = await medicalCodingService.calculateDRG({
        primaryDiagnosis: formData.primaryDiagnosis,
        secondaryDiagnoses: formData.secondaryDiagnoses,
        procedures: formData.procedures
      })
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          drgGroup: response.data.data
        }))
        enqueueSnackbar('DRG calculated successfully', { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar('Failed to calculate DRG', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      
      // Validate required fields
      if (!formData.primaryDiagnosis.icd10Code) {
        enqueueSnackbar('Primary diagnosis ICD-10 code is required', { variant: 'error' })
        return
      }

      // Check if clinical diagnosis is provided, if not we'll use the ICD description
      if (!formData.primaryDiagnosis.diagnosis && !formData.primaryDiagnosis.icd10Description) {
        enqueueSnackbar('Primary diagnosis description is required', { variant: 'error' })
        return
      }

      // Prepare data for submission
      const submissionData = {
        ...formData,
        // Ensure primary diagnosis has required fields
        primaryDiagnosis: {
          ...formData.primaryDiagnosis,
          diagnosis: formData.primaryDiagnosis.diagnosis || formData.primaryDiagnosis.icd10Description || 'Clinical diagnosis not specified'
        },
        // Add sequence numbers to secondary diagnoses
        secondaryDiagnoses: formData.secondaryDiagnoses.map((diagnosis, index) => ({
          ...diagnosis,
          sequence: index + 1,
          diagnosis: diagnosis.diagnosis || diagnosis.icd10Description || 'Secondary diagnosis'
        })),
        // Add sequence numbers to procedures
        procedures: formData.procedures.map((procedure, index) => ({
          ...procedure,
          sequence: index + 1,
          description: procedure.description || procedure.icd10pcsCode || 'Procedure'
        }))
      }

      const response = await medicalCodingService.createUpdateCoding(fileId, submissionData)
      
      if (response.data.success) {
        enqueueSnackbar('Medical coding saved successfully', { variant: 'success' })
        if (onBack) onBack()
      }
    } catch (error) {
      enqueueSnackbar('Failed to save medical coding', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const addSecondaryDiagnosis = () => {
    setFormData(prev => ({
      ...prev,
      secondaryDiagnoses: [
        ...prev.secondaryDiagnoses,
        {
          icd10Code: '',
          icd10Description: '',
          diagnosis: '',
          presentOnAdmission: true
        }
      ]
    }))
  }

  const removeSecondaryDiagnosis = (index) => {
    setFormData(prev => ({
      ...prev,
      secondaryDiagnoses: prev.secondaryDiagnoses.filter((_, i) => i !== index)
    }))
  }

  const addProcedure = () => {
    setFormData(prev => ({
      ...prev,
      procedures: [
        ...prev.procedures,
        {
          icd10pcsCode: '',
          description: '',
          procedureDate: new Date().toISOString().split('T')[0],
          surgeon: '',
          assistantSurgeon: ''
        }
      ]
    }))
  }

  const removeProcedure = (index) => {
    setFormData(prev => ({
      ...prev,
      procedures: prev.procedures.filter((_, i) => i !== index)
    }))
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Medical Coding
            </Typography>
            {isReadOnly && (
              <Chip 
                label="View Only" 
                color="info" 
                variant="outlined" 
                size="small"
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {mrdFile?.fileNumber} - {mrdFile?.patientName}
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Auto Suggestions */}
      {autoSuggestions.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Auto Suggestions Available
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {autoSuggestions.slice(0, 3).map((suggestion, index) => (
              <Chip
                key={index}
                label={`${suggestion.code} - ${suggestion.description?.slice(0, 30)}...`}
                size="small"
                onClick={() => handleICDSelect(suggestion)}
                icon={<AutoAwesome />}
              />
            ))}
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Primary Diagnosis */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Primary Diagnosis" 
              action={
                <Button
                  startIcon={<Search />}
                  onClick={() => {
                    setSearchType('primary')
                    setOpenICDSearch(true)
                  }}
                  disabled={isReadOnly}
                >
                  Search ICD
                </Button>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="ICD-10 Code"
                    value={formData.primaryDiagnosis.icd10Code}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      primaryDiagnosis: {
                        ...prev.primaryDiagnosis,
                        icd10Code: e.target.value
                      }
                    }))}
                    required
                    {...fieldProps}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="ICD-10 Description"
                    value={formData.primaryDiagnosis.icd10Description}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      primaryDiagnosis: {
                        ...prev.primaryDiagnosis,
                        icd10Description: e.target.value
                      }
                    }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Clinical Diagnosis"
                    value={formData.primaryDiagnosis.diagnosis}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      primaryDiagnosis: {
                        ...prev.primaryDiagnosis,
                        diagnosis: e.target.value
                      }
                    }))}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Secondary Diagnoses */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Secondary Diagnoses"
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<Search />}
                    onClick={() => {
                      setSearchType('secondary')
                      setOpenICDSearch(true)
                    }}
                    disabled={isReadOnly}
                  >
                    Search ICD
                  </Button>
                  <Button
                    startIcon={<Add />}
                    onClick={addSecondaryDiagnosis}
                    disabled={isReadOnly}
                  >
                    Add
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {formData.secondaryDiagnoses.map((diagnosis, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="ICD-10 Code"
                        value={diagnosis.icd10Code}
                        onChange={(e) => {
                          const newDiagnoses = [...formData.secondaryDiagnoses]
                          newDiagnoses[index].icd10Code = e.target.value
                          setFormData(prev => ({ ...prev, secondaryDiagnoses: newDiagnoses }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={diagnosis.icd10Description}
                        onChange={(e) => {
                          const newDiagnoses = [...formData.secondaryDiagnoses]
                          newDiagnoses[index].icd10Description = e.target.value
                          setFormData(prev => ({ ...prev, secondaryDiagnoses: newDiagnoses }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Clinical Diagnosis"
                        value={diagnosis.diagnosis}
                        onChange={(e) => {
                          const newDiagnoses = [...formData.secondaryDiagnoses]
                          newDiagnoses[index].diagnosis = e.target.value
                          setFormData(prev => ({ ...prev, secondaryDiagnoses: newDiagnoses }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        color="error"
                        onClick={() => removeSecondaryDiagnosis(index)}
                        disabled={isReadOnly}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              {formData.secondaryDiagnoses.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No secondary diagnoses added
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Procedures */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Procedures"
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    startIcon={<Search />}
                    onClick={() => {
                      setSearchType('procedure')
                      setOpenICDSearch(true)
                    }}
                    disabled={isReadOnly}
                  >
                    Search ICD-10-PCS
                  </Button>
                  <Button
                    startIcon={<Add />}
                    onClick={addProcedure}
                    disabled={isReadOnly}
                  >
                    Add
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {formData.procedures.map((procedure, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="ICD-10-PCS Code"
                        value={procedure.icd10pcsCode}
                        onChange={(e) => {
                          const newProcedures = [...formData.procedures]
                          newProcedures[index].icd10pcsCode = e.target.value
                          setFormData(prev => ({ ...prev, procedures: newProcedures }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={procedure.description}
                        onChange={(e) => {
                          const newProcedures = [...formData.procedures]
                          newProcedures[index].description = e.target.value
                          setFormData(prev => ({ ...prev, procedures: newProcedures }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Procedure Date"
                        value={procedure.procedureDate}
                        onChange={(e) => {
                          const newProcedures = [...formData.procedures]
                          newProcedures[index].procedureDate = e.target.value
                          setFormData(prev => ({ ...prev, procedures: newProcedures }))
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Surgeon"
                        value={procedure.surgeon}
                        onChange={(e) => {
                          const newProcedures = [...formData.procedures]
                          newProcedures[index].surgeon = e.target.value
                          setFormData(prev => ({ ...prev, procedures: newProcedures }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Assistant"
                        value={procedure.assistantSurgeon}
                        onChange={(e) => {
                          const newProcedures = [...formData.procedures]
                          newProcedures[index].assistantSurgeon = e.target.value
                          setFormData(prev => ({ ...prev, procedures: newProcedures }))
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        color="error"
                        onClick={() => removeProcedure(index)}
                        disabled={isReadOnly}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              {formData.procedures.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No procedures added
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* DRG Information */}
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="DRG Information"
              action={
                <Button
                  startIcon={<Calculate />}
                  onClick={handleCalculateDRG}
                  disabled={isReadOnly || !formData.primaryDiagnosis.icd10Code}
                >
                  Calculate DRG
                </Button>
              }
            />
            <CardContent>
              {formData.drgGroup ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="DRG Code"
                      value={formData.drgGroup.code || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="DRG Description"
                      value={formData.drgGroup.description || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="DRG Weight"
                      value={formData.drgGroup.weight || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  DRG not calculated yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>
          {mode === 'view' ? 'Back' : 'Cancel'}
        </Button>
        {mode === 'edit' && (
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={loading || !formData.primaryDiagnosis.icd10Code}
          >
            {existingCoding ? 'Update Coding' : 'Save Coding'}
          </Button>
        )}
      </Box>

      {/* ICD Search Dialog */}
      <Dialog
        open={openICDSearch}
        onClose={() => setOpenICDSearch(false)}
        maxWidth="lg"
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
      >
        <DialogTitle>
          ICD Code Search - {searchType === 'procedure' ? 'ICD-10-PCS' : 'ICD-10'}
        </DialogTitle>
        <DialogContent dividers>
          <ICDSearch 
            onSelect={handleICDSelect}
            version={searchType === 'procedure' ? 'ICD10-PCS' : 'ICD10'}
          />
        </DialogContent>
      </Dialog>

      {/* DRG Calculator Dialog */}
      <Dialog
        open={openDRGCalc}
        onClose={() => setOpenDRGCalc(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>DRG Calculator</DialogTitle>
        <DialogContent dividers>
          <DRGCalculator 
            codingData={formData}
            onCalculate={(drgData) => {
              setFormData(prev => ({ ...prev, drgGroup: drgData }))
              setOpenDRGCalc(false)
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default CodingForm